import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, BookOpen, Users, IndianRupee, Layers, PlayCircle,
  BarChart3, TrendingUp, Wallet, Calendar,
} from "lucide-react";
import { PageHeader, StatusBadge } from "../../ui";
import apiClient from "../../../utils/apiClient";
import "./sa-analytics.scss";

const TABS = [
  { key: "courses", label: "Courses", icon: BookOpen },
  { key: "students", label: "Students", icon: Users },
  { key: "income", label: "Income", icon: IndianRupee },
];

const getMonthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const getToday = () => new Date().toISOString().slice(0, 10);

const SAInstituteAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("courses");
  const [courses, setCourses] = useState(null);
  const [students, setStudents] = useState(null);
  const [income, setIncome] = useState(null);
  const [loading, setLoading] = useState(false);
  const [instituteName, setInstituteName] = useState("");
  const [dateFrom, setDateFrom] = useState(getMonthStart());
  const [dateTo, setDateTo] = useState(getToday());

  // Fetch tab data
  const fetchIncome = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const res = await apiClient.get(`/sa/analytics/${id}/income`, { params });
      setIncome(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [id, dateFrom, dateTo]);

  useEffect(() => {
    const fetchTab = async () => {
      setLoading(true);
      try {
        if (activeTab === "courses" && !courses) {
          const res = await apiClient.get(`/sa/analytics/${id}/courses`);
          setCourses(res.data);
        } else if (activeTab === "students" && !students) {
          const res = await apiClient.get(`/sa/analytics/${id}/students`);
          setStudents(res.data);
        } else if (activeTab === "income") {
          await fetchIncome();
          return;
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchTab();
  }, [activeTab, id, courses, students, fetchIncome]);

  // Fetch institute name from overview on mount
  useEffect(() => {
    const fetchName = async () => {
      try {
        const res = await apiClient.get("/sa/analytics/overview", { params: { search: id, limit: 1 } });
        const inst = res.data.institutes?.find((i) => i.instituteId === id);
        if (inst) setInstituteName(inst.name);
      } catch {
        // silent
      }
    };
    fetchName();
  }, [id]);

  const fmt = (v) => (v ?? 0).toLocaleString("en-IN");
  const fmtCurrency = (v, currency = "INR") => {
    const sym = currency === "INR" ? "₹" : "$";
    if (v >= 100000) return `${sym}${(v / 100000).toFixed(1)}L`;
    if (v >= 1000) return `${sym}${(v / 1000).toFixed(1)}K`;
    return `${sym}${fmt(v)}`;
  };

  return (
    <div className="sa-analytics">
      <PageHeader
        title={instituteName || "Institute Analytics"}
        description="Detailed course, student & income breakdown"
        icon={BarChart3}
        actions={
          <button className="sa-back-btn" onClick={() => navigate("/sa/analytics")}>
            <ArrowLeft size={14} /> Back to Overview
          </button>
        }
      />

      {/* Tabs */}
      <div className="sa-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`sa-tab ${activeTab === tab.key ? "sa-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Date range (shown on income tab) */}
      {activeTab === "income" && (
        <div className="sa-analytics-filters">
          <div className="sa-date-range">
            <Calendar size={14} />
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="sa-date-input" />
            <span className="sa-date-sep">to</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="sa-date-input" />
          </div>
        </div>
      )}

      {loading && <div className="sa-empty">Loading...</div>}

      {/* ── Courses Tab ── */}
      {activeTab === "courses" && courses && !loading && (
        <div className="sa-tab-content">
          <div className="sa-grid-3">
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Batch Courses</span>
                  <span className="sa-kpi-value">{fmt(courses.batchCourseCount)}</span>
                </div>
                <div className="sa-kpi-icon blue"><BookOpen size={20} /></div>
              </div>
            </div>
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Self-Paced Courses</span>
                  <span className="sa-kpi-value">{fmt(courses.selfPacedCourseCount)}</span>
                </div>
                <div className="sa-kpi-icon violet"><PlayCircle size={20} /></div>
              </div>
            </div>
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Total Batches</span>
                  <span className="sa-kpi-value">{fmt(courses.totalBatches)}</span>
                </div>
                <div className="sa-kpi-icon amber"><Layers size={20} /></div>
              </div>
            </div>
          </div>

          {/* Batch Courses Table */}
          {courses.batchCourses?.length > 0 && (
            <div className="sa-card">
              <div className="sa-card-head">
                <div className="sa-card-head-left">
                  <div className="sa-card-icon blue"><BookOpen size={18} /></div>
                  <div>
                    <div className="sa-card-title">Batch Courses</div>
                    <div className="sa-card-subtitle">Courses with batch-based delivery</div>
                  </div>
                </div>
              </div>
              <div className="sa-card-body sa-card-body--table">
                <table className="sa-mini-table">
                  <thead>
                    <tr>
                      <th>Course Name</th>
                      <th>Batches</th>
                      <th>Active Batches</th>
                      <th>Students</th>
                      <th>Fees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.batchCourses.map((c) => (
                      <tr key={c.courseId}>
                        <td className="sa-inst-name__text">{c.name}</td>
                        <td>{fmt(c.batches)}</td>
                        <td>{fmt(c.activeBatches)}</td>
                        <td>{fmt(c.totalStudents)}</td>
                        <td>{c.fees ? fmtCurrency(c.fees) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Self-Paced Courses Table */}
          {courses.selfPacedCourses?.length > 0 && (
            <div className="sa-card">
              <div className="sa-card-head">
                <div className="sa-card-head-left">
                  <div className="sa-card-icon violet"><PlayCircle size={18} /></div>
                  <div>
                    <div className="sa-card-title">Self-Paced Courses</div>
                    <div className="sa-card-subtitle">On-demand learning courses</div>
                  </div>
                </div>
              </div>
              <div className="sa-card-body sa-card-body--table">
                <table className="sa-mini-table">
                  <thead>
                    <tr>
                      <th>Course Name</th>
                      <th>Status</th>
                      <th>Enrollments</th>
                      <th>Active</th>
                      <th>Completed</th>
                      <th>Price</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.selfPacedCourses.map((c) => (
                      <tr key={c.courseId}>
                        <td className="sa-inst-name__text">{c.name}</td>
                        <td>
                          <StatusBadge status={c.isPublished ? "active" : "inactive"}>
                            {c.isPublished ? "Published" : "Draft"}
                          </StatusBadge>
                        </td>
                        <td>{fmt(c.enrollmentCount)}</td>
                        <td>{fmt(c.activeEnrollments)}</td>
                        <td>{fmt(c.completedEnrollments)}</td>
                        <td>{c.discountPrice ? fmtCurrency(c.discountPrice) : c.price ? fmtCurrency(c.price) : "Free"}</td>
                        <td>{c.totalRevenue > 0 ? fmtCurrency(c.totalRevenue) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Students Tab ── */}
      {activeTab === "students" && students && !loading && (
        <div className="sa-tab-content">
          <div className="sa-grid-2">
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Batch Students</span>
                  <span className="sa-kpi-value">{fmt(students.totalBatchStudents)}</span>
                </div>
                <div className="sa-kpi-icon blue"><Users size={20} /></div>
              </div>
            </div>
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Self-Paced Enrollments</span>
                  <span className="sa-kpi-value">{fmt(students.totalSelfPacedEnrollments)}</span>
                </div>
                <div className="sa-kpi-icon violet"><PlayCircle size={20} /></div>
              </div>
            </div>
          </div>

          {/* Students per Batch */}
          {students.studentsPerBatch?.length > 0 && (
            <div className="sa-card">
              <div className="sa-card-head">
                <div className="sa-card-head-left">
                  <div className="sa-card-icon blue"><Layers size={18} /></div>
                  <div>
                    <div className="sa-card-title">Students per Batch</div>
                    <div className="sa-card-subtitle">Batch-wise student distribution</div>
                  </div>
                </div>
              </div>
              <div className="sa-card-body sa-card-body--table">
                <table className="sa-mini-table">
                  <thead>
                    <tr>
                      <th>Batch</th>
                      <th>Course</th>
                      <th>Students</th>
                      <th>Max Students</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.studentsPerBatch.map((b) => (
                      <tr key={b.batchId}>
                        <td className="sa-inst-name__text">{b.batchName}</td>
                        <td>{b.courseName || "—"}</td>
                        <td>{fmt(b.studentCount)}</td>
                        <td>{b.maxStudents > 0 ? fmt(b.maxStudents) : "∞"}</td>
                        <td>
                          <StatusBadge status={b.status === "Ongoing" ? "active" : b.status === "Completed" ? "inactive" : "trial"}>
                            {b.status}
                          </StatusBadge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Self-Paced Enrollments */}
          {students.selfPacedEnrollments?.length > 0 && (
            <div className="sa-card">
              <div className="sa-card-head">
                <div className="sa-card-head-left">
                  <div className="sa-card-icon violet"><PlayCircle size={18} /></div>
                  <div>
                    <div className="sa-card-title">Self-Paced Enrollments</div>
                    <div className="sa-card-subtitle">Enrollment breakdown by course</div>
                  </div>
                </div>
              </div>
              <div className="sa-card-body sa-card-body--table">
                <table className="sa-mini-table">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Active</th>
                      <th>Completed</th>
                      <th>Expired</th>
                      <th>Refunded</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.selfPacedEnrollments.map((e) => (
                      <tr key={e.courseId}>
                        <td className="sa-inst-name__text">{e.courseName}</td>
                        <td>{fmt(e.active)}</td>
                        <td>{fmt(e.completed)}</td>
                        <td>{fmt(e.expired)}</td>
                        <td>{fmt(e.refunded)}</td>
                        <td><strong>{fmt(e.total)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Income Tab ── */}
      {activeTab === "income" && income && !loading && (
        <div className="sa-tab-content">
          <div className="sa-grid-3">
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Total Income</span>
                  <span className="sa-kpi-value">{fmtCurrency(income.totalIncome, income.currency)}</span>
                </div>
                <div className="sa-kpi-icon emerald"><IndianRupee size={20} /></div>
              </div>
            </div>
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Batch Course Revenue</span>
                  <span className="sa-kpi-value">{fmtCurrency(income.batchCourseIncome, income.currency)}</span>
                  <span className="sa-kpi-sub">{fmt(income.batchCourseTransactions)} transactions</span>
                </div>
                <div className="sa-kpi-icon blue"><BookOpen size={20} /></div>
              </div>
            </div>
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Self-Paced Revenue</span>
                  <span className="sa-kpi-value">{fmtCurrency(income.selfPacedRevenue, income.currency)}</span>
                  <span className="sa-kpi-sub">{fmt(income.selfPacedEnrollments)} enrollments</span>
                </div>
                <div className="sa-kpi-icon violet"><PlayCircle size={20} /></div>
              </div>
            </div>
          </div>

          {/* Payment Method Breakdown */}
          <div className="sa-grid-3">
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Manual Collections</span>
                  <span className="sa-kpi-value">{fmtCurrency(income.manualCollections, income.currency)}</span>
                  <span className="sa-kpi-sub">{fmt(income.manualTransactions)} txns</span>
                </div>
                <div className="sa-kpi-icon amber"><Wallet size={20} /></div>
              </div>
            </div>
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Online (Own Gateway)</span>
                  <span className="sa-kpi-value">{fmtCurrency(income.onlineOwnGateway, income.currency)}</span>
                  <span className="sa-kpi-sub">{fmt(income.onlineOwnGatewayTransactions)} txns</span>
                </div>
                <div className="sa-kpi-icon violet"><TrendingUp size={20} /></div>
              </div>
            </div>
            <div className="sa-kpi-card">
              <div className="sa-kpi-inner">
                <div className="sa-kpi-info">
                  <span className="sa-kpi-label">Online (LMS Payment)</span>
                  <span className="sa-kpi-value">{fmtCurrency(income.onlineLmsPayment, income.currency)}</span>
                  <span className="sa-kpi-sub">{fmt(income.onlineLmsTransactions)} txns</span>
                </div>
                <div className="sa-kpi-icon amber"><TrendingUp size={20} /></div>
              </div>
            </div>
          </div>

          {/* Commission & Payout Section */}
          {(income.platformCommission > 0 || income.pendingPayout > 0 || income.settledPayout > 0) && (
            <div className="sa-grid-4">
              <div className="sa-kpi-card">
                <div className="sa-kpi-inner">
                  <div className="sa-kpi-info">
                    <span className="sa-kpi-label">Platform Commission</span>
                    <span className="sa-kpi-value">{fmtCurrency(income.platformCommission, income.currency)}</span>
                  </div>
                  <div className="sa-kpi-icon rose"><IndianRupee size={20} /></div>
                </div>
              </div>
              <div className="sa-kpi-card">
                <div className="sa-kpi-inner">
                  <div className="sa-kpi-info">
                    <span className="sa-kpi-label">Gateway Fees</span>
                    <span className="sa-kpi-value">{fmtCurrency(income.gatewayFees, income.currency)}</span>
                  </div>
                  <div className="sa-kpi-icon amber"><IndianRupee size={20} /></div>
                </div>
              </div>
              <div className="sa-kpi-card">
                <div className="sa-kpi-inner">
                  <div className="sa-kpi-info">
                    <span className="sa-kpi-label">Pending Payout</span>
                    <span className="sa-kpi-value">{fmtCurrency(income.pendingPayout, income.currency)}</span>
                  </div>
                  <div className="sa-kpi-icon amber"><Wallet size={20} /></div>
                </div>
              </div>
              <div className="sa-kpi-card">
                <div className="sa-kpi-inner">
                  <div className="sa-kpi-info">
                    <span className="sa-kpi-label">Settled Payout</span>
                    <span className="sa-kpi-value">{fmtCurrency(income.settledPayout, income.currency)}</span>
                  </div>
                  <div className="sa-kpi-icon emerald"><Wallet size={20} /></div>
                </div>
              </div>
            </div>
          )}

          {/* Self-Paced Revenue — removed as now shown in primary KPI row */}

          {/* Monthly Trend */}
          {income.monthlyTrend?.length > 0 && (
            <div className="sa-card">
              <div className="sa-card-head">
                <div className="sa-card-head-left">
                  <div className="sa-card-icon emerald"><BarChart3 size={18} /></div>
                  <div>
                    <div className="sa-card-title">Monthly Income Trend</div>
                    <div className="sa-card-subtitle">Last 12 months</div>
                  </div>
                </div>
              </div>
              <div className="sa-card-body sa-card-body--table">
                <table className="sa-mini-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Batch Course</th>
                      <th>Self-Paced</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {income.monthlyTrend.map((m) => (
                      <tr key={m.month}>
                        <td className="sa-inst-name__text">{m.month}</td>
                        <td>{fmtCurrency(m.batchCourse, income.currency)}</td>
                        <td>{fmtCurrency(m.selfPaced, income.currency)}</td>
                        <td><strong>{fmtCurrency(m.total, income.currency)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SAInstituteAnalytics;
